import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import WizardSelect from '../../components/ui/WizardSelect';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import {
  getCivilAdviseRequestViewData,
  saveCivilAdviseRequest,
  type CivilAdviseRequestViewData,
} from '../../services/civilAdviseCaseRequests';
import type { CivilAdviseWizardStep } from '../../pages/wizard/civilAdvise/civilAdviseConstants';
import type { CivilAdviseWizardDraft } from '../../types/civilAdviseCaseRequest';
import { CIVIL_ADVISE_FIELD_LABELS as L } from '../../pages/wizard/civilAdvise/civilAdviseFieldLabels';
import { isOtherCorporateInstitute } from '../../pages/wizard/civilAdvise/civilAdviseHelpers';
import {
  isHighUrgencyLevel,
  minDeadlineDateForCivilAdvise,
  validateCivilAdviseStep,
} from '../../pages/wizard/civilAdvise/civilAdviseStepValidation';
import { useCivilAdviseLookups } from '../../pages/wizard/civilAdvise/useCivilAdviseLookups';
import { ReviewUploadedFilesTable } from '../../components/wizard/ReviewUploadedFilesTable';
import {
  deliveryMethodDisplayFromStep3,
  submitterFromStep3,
} from '../view-request/viewRequestHelpers';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { sliceMax } from '../../utils/caseFieldValidation';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import { FieldError, inpClass } from './criminalFieldUi';
import { omitCaseUpdateUploadErrors, omitCaseUpdateUploadSpecs } from './caseUpdateUploadValidation';
import {
  formatSubmittedAtLabel,
  confirmCaseUpdateCancel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: CivilAdviseRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_TEXT = 2000;

function cloneDraft(d: CivilAdviseWizardDraft): CivilAdviseWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    previousAdviceFiles: d.previousAdviceFiles.map((f) => ({ ...f })),
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
    documents: d.documents.map((doc) => ({
      ...doc,
      files: doc.files.map((f) => ({ ...f })),
    })),
  };
}

function draftHasUnsavedChanges(current: CivilAdviseWizardDraft, saved: CivilAdviseWizardDraft): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function pickStep1Snapshot(d: CivilAdviseWizardDraft) {
  return {
    description: d.description,
    provinceId: d.provinceId,
    districtId: d.districtId,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    hasPreviousAdvice: d.hasPreviousAdvice,
    hasConnectedCaseRelevant: d.hasConnectedCaseRelevant,
    connectedCaseDetails: d.connectedCaseDetails,
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRef: d.connectedAgdFileRef,
    instituteType: d.instituteType,
    corporateInstituteId: d.corporateInstituteId,
    corporateInstituteOther: d.corporateInstituteOther,
  };
}

function pickStep2Snapshot(d: CivilAdviseWizardDraft) {
  return {
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

function stepHasData(step: 1 | 2, d: CivilAdviseWizardDraft): boolean {
  if (step === 1) {
    return !!(
      d.description.trim() ||
      d.yourReferenceNumber.trim() ||
      d.provinceId.trim() ||
      d.caseNumber.trim()
    );
  }
  return !!(d.urgencyLevelId.trim() || d.deadline.trim() || d.urgencyJustification.trim());
}

function canSaveStep(
  step: 1 | 2,
  draft: CivilAdviseWizardDraft | null,
  initial: CivilAdviseWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pick = step === 1 ? pickStep1Snapshot : pickStep2Snapshot;
  const changed = JSON.stringify(pick(draft)) !== JSON.stringify(pick(initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

export default function TaskCaseCivilAdviseUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const [draft, setDraft] = useState<CivilAdviseWizardDraft | null>(null);
  const [initial, setInitial] = useState<CivilAdviseWizardDraft | null>(null);
  const [step3, setStep3] = useState<CivilAdviseRequestViewData['step3']>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<CivilAdviseWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSaving = savingStep !== null;

  const lookups = useCivilAdviseLookups('review', draft?.provinceId ?? '');
  const minDeadlineDate = useMemo(() => minDeadlineDateForCivilAdvise(), []);
  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep3(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setStep3(viewData.step3);
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getCivilAdviseRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setStep3(undefined);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setStep3(res.data.step3);
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const canSaveStep1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const saveSection = async (step: 1 | 2) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const result = validateCivilAdviseStep(
      step,
      draft,
      [],
      lookups.corporateInstitutes,
    );
    const errors = omitCaseUpdateUploadErrors(result.errors);
    const specs = omitCaseUpdateUploadSpecs(result.specs);
    setErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstInvalid(specs, errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveCivilAdviseRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        const pick = step === 1 ? pickStep1Snapshot : pickStep2Snapshot;
        return mergeSavedStepIntoInitialBaseline(prev, next, pick, cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const submitter = submitterFromStep3(step3);
  const deliveryLabel = deliveryMethodDisplayFromStep3(
    draft as unknown as CriminalWizardDraft,
    lookups.deliveryMethods,
    step3,
  );
  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at: <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
              <ReviewField label="Institute Type" value={instituteLabel} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Case Intake Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Description <span className="req">*</span>
              </label>
              <textarea
                id="civ-description"
                className={inpClass(Boolean(errors.description))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.description}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('description');
                }}
              />
              <FieldError message={errors.description} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>Province</label>
                <WizardSelect
                  value={draft.provinceId}
                  disabled={readOnly}
                  placeholder="Select Province"
                  options={toSelectOpts(lookups.provinces)}
                  onChange={(id) => {
                    const name = lookups.provinces.find((p) => p.id === id)?.name ?? '';
                    setDraft((d) =>
                      d ? { ...d, provinceId: id, provinceName: name, districtId: '', districtName: '' } : d,
                    );
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>District</label>
                <WizardSelect
                  value={draft.districtId}
                  disabled={readOnly || !draft.provinceId}
                  placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                  options={toSelectOpts(lookups.districts)}
                  onChange={(id) => {
                    const name = lookups.districts.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, districtId: id, districtName: name } : d));
                  }}
                />
              </div>
              <div className="ccr-fg">
                <label>
                  Your Reference Number (External) <span className="req">*</span>
                </label>
                <input
                  id="civ-yourRef"
                  className={inpClass(Boolean(errors.yourReferenceNumber))}
                  value={draft.yourReferenceNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('yourReferenceNumber');
                  }}
                />
                <FieldError message={errors.yourReferenceNumber} />
              </div>
              <div className="ccr-fg">
                <label>Other External Reference Number(s)</label>
                <input
                  className={inpClass(false)}
                  value={draft.otherExternalReferences}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>
                  {L.hasPreviousAdvice} <span className="req">*</span>
                </label>
                <div id="civ-previousAdvice" className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="civ-previousAdvice"
                        checked={draft.hasPreviousAdvice === v}
                        /* Intake history — Case Update must not clear previous-advice documents. */
                        disabled
                        readOnly
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasPreviousAdvice} />
              </div>
              {draft.hasPreviousAdvice === 'yes' || draft.previousAdviceFiles.length > 0 ? (
                <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                  <label>{L.previousAdviceAttachment}</label>
                  <ReviewUploadedFilesTable
                    files={draft.previousAdviceFiles}
                    emptyMessage="—"
                  />
                </div>
              ) : null}
              <div className="ccr-fg">
                <label>
                  {L.connectedCaseRelevant} <span className="req">*</span>
                </label>
                <div id="civ-connectedCase" className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="civ-connectedCase"
                        checked={draft.hasConnectedCaseRelevant === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  hasConnectedCaseRelevant: v,
                                  connectedCaseDetails: v === 'yes' ? d.connectedCaseDetails : '',
                                }
                              : d,
                          );
                          clearErr('hasConnectedCaseRelevant');
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasConnectedCaseRelevant} />
              </div>
              {draft.hasConnectedCaseRelevant === 'yes' ? (
                <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    {L.connectedCaseDetails} <span className="req">*</span>
                  </label>
                  <textarea
                    id="civ-connectedCaseDetails"
                    className={inpClass(Boolean(errors.connectedCaseDetails))}
                    rows={3}
                    maxLength={MAX_DESC}
                    value={draft.connectedCaseDetails}
                    readOnly={readOnly}
                    disabled={readOnly}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, connectedCaseDetails: sliceMax(e.target.value, MAX_DESC) } : d,
                      );
                      clearErr('connectedCaseDetails');
                    }}
                  />
                  <FieldError message={errors.connectedCaseDetails} />
                </div>
              ) : null}
              <div className="ccr-fg">
                <label>{L.caseNumber}</label>
                <input
                  className={inpClass(false)}
                  value={draft.caseNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d))
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>
                  {L.connectedMatters} <span className="req">*</span>
                </label>
                <div id="civ-connectedMatters" className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="civ-connectedMatters"
                        checked={draft.hasConnectedMatters === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  hasConnectedMatters: v,
                                  connectedCaseNumber: v === 'yes' ? d.connectedCaseNumber : '',
                                  connectedAgdFileRef: v === 'yes' ? d.connectedAgdFileRef : '',
                                }
                              : d,
                          );
                          clearErr('hasConnectedMatters');
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasConnectedMatters} />
              </div>
              {draft.hasConnectedMatters === 'yes' ? (
                <>
                  <div className="ccr-fg">
                    <label>
                      {L.connectedCaseNumber} <span className="req">*</span>
                    </label>
                    <input
                      id="civ-connectedCaseNo"
                      className={inpClass(Boolean(errors.connectedCaseNumber))}
                      value={draft.connectedCaseNumber}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedCaseNumber');
                      }}
                    />
                    <FieldError message={errors.connectedCaseNumber} />
                  </div>
                  <div className="ccr-fg">
                    <label>{L.connectedAgdFileRef}</label>
                    <input
                      id="civ-connectedAgd"
                      className={inpClass(Boolean(errors.connectedAgdFileRef))}
                      value={draft.connectedAgdFileRef}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d
                            ? {
                                ...d,
                                connectedAgdFileRef: sliceMax(e.target.value, MAX_REF),
                                connectedAgdFileRefAvailable: true,
                              }
                            : d,
                        );
                        clearErr('connectedAgdFileRef');
                      }}
                    />
                    <FieldError message={errors.connectedAgdFileRef} />
                  </div>
                </>
              ) : null}
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep1}
            saving={savingStep === 1}
            readOnly={readOnly}
            onSave={() => void saveSection(1)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 02" title="Timeline">
          <ReviewSubBlock title="Timeline">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Urgency Level <span className="req">*</span>
                </label>
                <WizardSelect
                  id="civ-urgency"
                  hasError={Boolean(errors.urgencyLevelId)}
                  value={draft.urgencyLevelId}
                  disabled={readOnly}
                  placeholder="Select Urgency Level"
                  options={toSelectOpts(lookups.urgencyLevels)}
                  onChange={(id) => {
                    const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d));
                    if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                    clearErr('urgencyLevelId');
                  }}
                />
                <FieldError message={errors.urgencyLevelId} />
              </div>
              <div className="ccr-fg">
                <label>Deadline (if any)</label>
                <FormattedDateInput
                  id="civ-deadline"
                  className={inpClass(Boolean(errors.deadline))}
                  value={draft.deadline}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, deadline: next } : d));
                    clearErr('deadline');
                  }}
                />
                <FieldError message={errors.deadline} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>
                Urgency Justification {urgencyJustificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="civ-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('urgencyJustification');
                }}
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep2}
            saving={savingStep === 2}
            readOnly={readOnly}
            onSave={() => void saveSection(2)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 03" title="Submission & Physical File Delivery">
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField label="Submitted Agency Name" value={submitter.agency} />
              <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
              <ReviewField label="Designation" value={submitter.designation} />
              <ReviewField label="Submitted by" value={submitter.name} />
              <ReviewField label="Email Address" value={submitter.email} />
              <ReviewField label="Contact Number" value={submitter.contact} />
            </ReviewGrid>
          </ReviewSubBlock>
          <ReviewSubBlock title="Physical File Delivery">
            <ReviewField label="Delivery Method" value={deliveryLabel} />
            {draft.deliveryOther.trim() ? (
              <ReviewField label="Other Delivery Method" value={draft.deliveryOther} />
            ) : null}
          </ReviewSubBlock>
        </ReviewSection>
      </div>
    </>
  );
}
